import { AnimatePresence, motion } from 'framer-motion';
import { ImageIcon } from 'lucide-react';
import React, { useState } from 'react';
import LoadingComponent from './LoadingComponent';

export interface ImageObject {
  secure_url: string;
}

interface PlaceholderColors {
  from: string;
  to: string;
  iconBackground: string;
}

interface ImageWithPlaceholderProps {
  image: ImageObject | null;
  alt: string;
  className?: string;
  placeholderClassName?: string;
  placeholderColors?: PlaceholderColors;
}

// Default theme colors
const defaultPlaceholderColors: PlaceholderColors = {
  from: 'rgba(0, 0, 0, 0.4)',
  to: 'rgba(0, 0, 0, 0.4)',
  iconBackground: 'transparent'
};

const ImageWithPlaceholder: React.FC<ImageWithPlaceholderProps> = ({
  image,
  alt,
  className = '',
  placeholderClassName = '',
  placeholderColors
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  // Use provided colors or defaults
  const colors = placeholderColors || defaultPlaceholderColors;

  const handleLoad = () => {
    setIsLoading(false);
  };

  const handleError = () => {
    setIsLoading(false);
    setHasError(true);
  };

  // Create gradient style based on colors
  const gradientStyle = {
    background: colors.from === colors.to
      ? colors.from
      : `linear-gradient(to bottom, ${colors.from}, ${colors.to})`
  };

  // Style for icon container
  const iconStyle = {
    background: colors.iconBackground
  };

  return (
    <div className="lp-relative lp-w-full lp-h-full">
      <AnimatePresence mode="wait">
        {(!image || !image.secure_url || hasError) ? (
          <motion.div
            key="placeholder"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            style={gradientStyle}
            className={`lp-w-full lp-h-full lp-flex lp-flex-col lp-items-center lp-justify-center ${placeholderClassName}`}
            aria-label="Business image placeholder"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.1, duration: 0.3 }}
              style={iconStyle}
              className="lp-flex lp-flex-col lp-items-center lp-justify-center lp-p-4 lp-rounded-lg"
            >
              <ImageIcon className="lp-w-12 lp-h-12 lp-text-gray-400 lp-mb-3" />
              <div className="lp-text-center lp-px-4">
                <p className="lp-text-gray-300 lp-text-sm">
                  Business hasn't uploaded
                </p>
                <p className="lp-text-gray-300 lp-text-sm">
                  an image yet
                </p>
                <p className="lp-text-gray-400 lp-text-xs lp-mt-1">
                  You can still book this service
                </p>
              </div>
            </motion.div>
          </motion.div>
        ) : (
          <motion.div
            key="image-container"
            className="lp-relative lp-w-full lp-h-full"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <motion.img
              src={image.secure_url}
              alt={alt}
              className={`lp-w-full lp-h-full lp-object-cover ${className}`}
              initial={{ scale: 1.1, opacity: 0 }}
              animate={{
                scale: 1,
                opacity: isLoading ? 0 : 1
              }}
              transition={{
                duration: 0.4,
                ease: [0.23, 1, 0.32, 1]
              }}
              onLoad={handleLoad}
              onError={handleError}
              loading="lazy"
            />
            <AnimatePresence>
              {isLoading && (
                <motion.div
                  key="loader"
                  className="lp-absolute lp-inset-0 lp-bg-black/40"
                  initial={{ opacity: 1 }}
                  exit={{
                    opacity: 0,
                    transition: {
                      duration: 0.3,
                      ease: "easeOut"
                    }
                  }}
                >
                  <motion.div
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.9, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <LoadingComponent />
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ImageWithPlaceholder;
